import Landing from "components/authentication/portal/Landing";
import Login from "components/authentication/portal/Login";
import VerifyCode from "components/authentication/portal/VerifyCode";
import PortalPanel from "components/shared/PortalPanel";
import {
  Auth0Errors,
  LoginScopeEnum,
  LoginVerificationType,
} from "models/Constants";
import * as React from "react";
import { Redirect } from "react-router";
import { StorageKeys } from "utils/Authentication";
import authService from "utils/AuthService";
import { clearSessionMetadata } from "utils/Helpers";
import history from "utils/history";
import { portalLoginSvg } from "utils/StaticPaths";

const LoginContainer: React.FC = () => {
  const [email, setEmail] = React.useState("");
  const [loginStep, setLoginStep] = React.useState(1);
  const [hasErrors, setHasErrors] = React.useState(false);
  const [codeResendTimer, setCodeResendTimer] =
    React.useState<NodeJS.Timeout>();
  const [
    secondsRemainingToAllowCodeResend,
    setSecondsRemainingToAllowCodeResend,
  ] = React.useState(0);

  const redirectTo = !authService.getEmail() ? "/" : undefined;
  React.useEffect(() => {
    const redirectTo = sessionStorage.getItem(StorageKeys.RedirectTo);
    if (!redirectTo) {
      sessionStorage.setItem(StorageKeys.RedirectTo, "/me/welcome");
    }

    if (localStorage.getItem("loginStep") === "2") {
      setLoginStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (secondsRemainingToAllowCodeResend <= 0 && codeResendTimer) {
      clearInterval(codeResendTimer);
    }
  }, [codeResendTimer, secondsRemainingToAllowCodeResend]);

  const goBack = () => {
    if (loginStep === 3) {
      setLoginStep(2);
      return;
    }
  };

  const verifyCode = async (code: string) => {
    const response = await authService.passwordLessSignInVerifyCode({
      method: LoginVerificationType.Email,
      code,
      scope: LoginScopeEnum.Portal,
    });
    if (response?.ok) {
      authService.saveAuthMetadata(response.payload.signature);
      if (authService.getIsAuthenticated()) {
        const redirectTo = sessionStorage.getItem(StorageKeys.RedirectTo);
        if (redirectTo) {
          sessionStorage.removeItem(StorageKeys.RedirectTo);
          sessionStorage.removeItem(StorageKeys.HasLogin);
          sessionStorage.removeItem(StorageKeys.AuthId);
          sessionStorage.removeItem(StorageKeys.RetrievalId);
          history.push(redirectTo);
        }
        clearSessionMetadata();
        localStorage.removeItem("loginStep");
      }
    } else {
      if (response?.errors) {
        const errorMessages = response?.errors.map((m) => m.message);
        if (
          errorMessages.includes(Auth0Errors.ExpiredCode) ||
          errorMessages.includes(Auth0Errors.InvalidCode)
        )
          setHasErrors(true);
      }
    }
  };

  const sendCode = async () => {
    if (secondsRemainingToAllowCodeResend <= 0 || authService.getEmail() !== email) {
      authService.setEmail(email);
      authService.setKey(email);
      const response = await authService.passwordLessSignInStart({
        method: LoginVerificationType.Email,
      });
      if (!response?.ok && response?.status === 404) {
        setLoginStep(4)
      }
      setSecondsRemainingToAllowCodeResend(60);
      const timer = setInterval(() => {
        setSecondsRemainingToAllowCodeResend((prevValue) => prevValue - 1);
      }, 1000);
      setCodeResendTimer(timer);
    }
  };

  return (
    <div className="h-100 portal-login-backdrop">
      {redirectTo && <Redirect to={redirectTo} />}
      {loginStep === 1 && (
        <Landing imagePath={portalLoginSvg} onGoNext={() => setLoginStep(2)} />
      )}
      {loginStep === 2 && (
        <Login
          email={email}
          onGoNext={() => setLoginStep(3)}
          onEmailChange={setEmail}
          imagePath={portalLoginSvg}
        />
      )}
      {loginStep === 3 && (
        <VerifyCode
          email={email}
          onGoBack={goBack}
          onSendCode={sendCode}
          onVerifyCode={verifyCode}
          secondsRemainingToAllowCodeResend={secondsRemainingToAllowCodeResend}
          showErrors={hasErrors}
          imagePath={portalLoginSvg}
        />
      )}
      {loginStep === 4 && (
        <PortalPanel
          image={portalLoginSvg}
          primaryButton={{
            text: `Try again`,
            action: () => {
              setLoginStep(2);
              setSecondsRemainingToAllowCodeResend(0);
            },
          }}
          secondaryButton={{
            action: () => window.open("https://www.marbleapi.com", "_self"),
            text: "Visit Marble website",
          }}
        >
          <div
            className="text-center"
            style={{ fontSize: 20, fontWeight: 600, lineHeight: "23px", marginBottom: "1rem" }}>
            {email} is not registered with Marble. If you are
            a Marble user, make sure the email is entered correctly.
          </div>
        </PortalPanel>
      )}
    </div>
  );
};

export default LoginContainer;
